import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';

const FEE_HANDLE = 'amada-hand-engraving';

/** @type {typeof fetch | null} */
let nativeFetch = null;
let syncing = false;

function root() {
  const dialog = document.querySelector('quick-add-dialog dialog[open], .quick-add-modal[open]');
  if (dialog) {
    return dialog.querySelector('[data-amada-personalise]') || dialog.closest('quick-add-dialog')?.querySelector('[data-amada-personalise]');
  }
  return document.querySelector('[data-amada-personalise]');
}

function engravingText() {
  const input = root()?.querySelector('input, textarea');
  return String(input?.value || '').trim();
}

function feeVariantId() {
  return root()?.dataset.feeVariantId || '';
}

function hasEngraving() {
  return engravingText().length > 0;
}

function isEngravingConfirmed() {
  const host = root();
  return Boolean(host?.dataset.engravingConfirmed === 'true' && hasEngraving());
}

/**
 * @param {Element} el
 * @param {string} formatted
 */
function setPriceText(el, formatted) {
  let replaced = false;
  [...el.childNodes].forEach((node) => {
    if (node.nodeType !== Node.TEXT_NODE) return;
    if (!node.textContent?.trim()) return;
    node.textContent = replaced ? '' : ` ${formatted}`;
    replaced = true;
  });
  if (!replaced) {
    el.append(` ${formatted}`);
  }
}

function applyPrice() {
  const host = root();
  if (!host) return;
  const fee = Number(host.dataset.feeCents || 0);
  const format = host.dataset.moneyFormat || '£{{amount}}';
  const currency = host.dataset.currency || 'GBP';
  const on = isEngravingConfirmed();

  if (on) {
    host.dataset.engravingActive = 'true';
  } else {
    delete host.dataset.engravingActive;
  }

  // Main product price stays the catalogue price. Only Add to Cart buttons include the fee.
  const scope =
    host.closest('quick-add-dialog, dialog, .shopify-section, [id^="shopify-section-"]') || document;
  const priceHost = scope.querySelector('product-price[data-amada-base-cents]') || document.querySelector('product-price[data-amada-base-cents]');
  const base = Number(priceHost?.getAttribute('data-amada-base-cents') || host.dataset.productCents || 0);
  if (!base) return;

  const total = formatMoney(on ? base + fee : base, format, currency);
  scope.querySelectorAll(
    'button[name="add"] .button-text, button.add-to-cart-button .button-text, .add-to-cart-button .button-text, [data-testid="standalone-add-to-cart"] .button-text, [data-add-to-cart-label]'
  ).forEach((el) => {
    const current = el.textContent?.trim() || '';
    if (/choose a ring size/i.test(current)) return;
    const label = el.dataset.amadaBaseLabel || current.replace(/\s*·\s*£[\d,.]+.*$/, '').trim() || 'Add to Bag';
    el.dataset.amadaBaseLabel = label;
    el.textContent = on ? `${label} · ${total}` : current.includes('·') ? `${label} · ${formatMoney(base, format, currency)}` : label;
  });
}

function toggleAccelerated() {
  const hide = isEngravingConfirmed();
  document.querySelectorAll('shopify-accelerated-checkout').forEach((el) => {
    el.toggleAttribute('hidden', hide);
  });
}

function onEngravingInput() {
  const host = root();
  if (host && !hasEngraving()) delete host.dataset.engravingConfirmed;
  applyPrice();
  toggleAccelerated();
  syncConfirmButton();
  updateCharacterCount();
}

function confirmButton(host) {
  return host?.querySelector?.('[data-amada-engraving-confirm]');
}

function updateCharacterCount() {
  const host = root();
  const input = host?.querySelector?.('input, textarea');
  const count = host?.querySelector?.('[ref="characterCount"], .__character-count');
  if (!(input instanceof HTMLElement) || !(count instanceof HTMLElement)) return;
  const currentLength = String(input.value || '').length;
  const maxLength = Number(input.getAttribute('maxlength') || count.getAttribute('data-max') || 25);
  const template = count.getAttribute('data-template');
  if (template && !host.hasAttribute('data-amada-personalise')) {
    count.textContent = template.replace('[current]', String(currentLength)).replace('[max]', String(maxLength));
    return;
  }
  count.textContent = `${currentLength}/${maxLength}`;
}

function syncConfirmButton() {
  const host = root();
  const btn = confirmButton(host);
  if (!(btn instanceof HTMLElement)) return;
  const hasText = hasEngraving();
  const confirmed = host?.dataset.engravingConfirmed === 'true';
  btn.hidden = !hasText || confirmed;
  if (!btn.classList.contains('is-loading')) {
    btn.disabled = false;
  }
}

function setConfirmLoading(loading) {
  const host = root();
  const btn = confirmButton(host);
  if (!(btn instanceof HTMLButtonElement)) return;
  btn.classList.toggle('is-loading', Boolean(loading));
  btn.disabled = Boolean(loading);
  btn.setAttribute('aria-busy', loading ? 'true' : 'false');
  if (loading) btn.hidden = false;
}

function confirmEngraving() {
  const host = root();
  if (!host || host.dataset.engravingBusy === 'true') return;
  if (!hasEngraving()) {
    syncConfirmButton();
    return;
  }
  host.dataset.engravingBusy = 'true';
  setConfirmLoading(true);
  window.setTimeout(() => {
    host.dataset.engravingConfirmed = 'true';
    applyPrice();
    toggleAccelerated();
    setConfirmLoading(false);
    syncConfirmButton();
    delete host.dataset.engravingBusy;
    host.dispatchEvent(new Event('change', { bubbles: true }));
  }, 350);
}

function bindField() {
  const host = root();
  if (!host) return;
  const input = host.querySelector('input, textarea');
  if (input && !input.dataset.amadaPersonaliseBound) {
    input.dataset.amadaPersonaliseBound = 'true';
    input.addEventListener('input', () => {
      delete host.dataset.engravingConfirmed;
      onEngravingInput();
    });
    input.addEventListener('change', () => {
      delete host.dataset.engravingConfirmed;
      onEngravingInput();
    });
  }
  const btn = confirmButton(host);
  if (btn instanceof HTMLButtonElement && !btn.dataset.amadaPersonaliseBound) {
    btn.dataset.amadaPersonaliseBound = 'true';
    btn.addEventListener('click', (event) => {
      event.preventDefault();
      confirmEngraving();
    });
  }
  onEngravingInput();
}

/**
 * @param {string} key
 * @param {number} quantity
 */
async function changeLine(key, quantity, properties) {
  if (!nativeFetch) return;
  const body = { id: key, quantity };
  if (properties) body.properties = properties;
  await nativeFetch(`${(typeof Theme !== 'undefined' && Theme.routes?.cart_url) || '/cart'}/change.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
    credentials: 'same-origin',
  });
}

function engravingTextFromProps(props) {
  if (!props || typeof props !== 'object') return '';
  return String(props.Engraving || props.engraving || props['Engraving text'] || '').trim();
}

async function syncFeeLines() {
  if (syncing || !nativeFetch) return;
  if (document.querySelector('.amada-cart')) return;
  syncing = true;
  try {
    const cart = await (await nativeFetch(`${(typeof Theme !== 'undefined' && Theme.routes?.cart_url) || '/cart'}.js`, { credentials: 'same-origin' })).json();
    const items = cart.items || [];
    /** @type {Record<string, number>} */
    const need = {};
    for (const item of items) {
      if (item.handle === FEE_HANDLE) continue;
      if (!engravingTextFromProps(item.properties)) continue;
      const vid = String(item.variant_id);
      need[vid] = (need[vid] || 0) + Number(item.quantity || 1);
    }

    const feeItems = items.filter((item) => item.handle === FEE_HANDLE);
    /** @type {Record<string, any[]>} */
    const byOwner = {};
    const unlinked = [];
    for (const fee of feeItems) {
      const owner = String(fee.properties?._for || '').trim();
      if (owner) (byOwner[owner] ||= []).push(fee);
      else unlinked.push(fee);
    }

    const usedUnlinked = new Set();
    const owners = new Set([...Object.keys(need), ...Object.keys(byOwner)]);

    for (const owner of owners) {
      const want = Number(need[owner] || 0);
      const lines = byOwner[owner] || [];
      if (want === 0) {
        for (const line of lines) await changeLine(line.key, 0);
        continue;
      }
      if (!lines.length) {
        const spare = unlinked.find((line) => !usedUnlinked.has(line.key));
        if (spare) {
          usedUnlinked.add(spare.key);
          await changeLine(spare.key, want, { _for: owner });
        }
        continue;
      }
      const primary = lines[0];
      if (Number(primary.quantity) !== want) {
        await changeLine(primary.key, want, { _for: owner });
      }
      for (const extra of lines.slice(1)) await changeLine(extra.key, 0);
    }

    for (const spare of unlinked) {
      if (usedUnlinked.has(spare.key)) continue;
      await changeLine(spare.key, 0);
    }
  } catch {
    // Cart sync is best-effort; checkout still has the lines from add-to-cart.
  } finally {
    syncing = false;
  }
}

/**
 * @param {FormData} formData
 */
function jewelryProperties(formData) {
  /** @type {Record<string, string>} */
  const properties = {};
  for (const [key, value] of formData.entries()) {
    const match = key.match(/^properties\[([^\]]+)\]$/);
    if (!match || value == null) continue;
    const text = String(value).trim();
    if (text) properties[match[1]] = text;
  }

  // Live engraving field is often outside <form> (Quick Add). Always prefer it.
  const host = root();
  const input = host?.querySelector('input, textarea');
  const liveText = String(input?.value || '').trim();
  if (liveText && isEngravingConfirmed()) {
    const named = input?.getAttribute('name')?.match(/^properties\[([^\]]+)\]$/);
    properties[named?.[1] || 'Engraving'] = liveText;
  } else {
    for (const key of Object.keys(properties)) {
      if (String(key).toLowerCase().includes('engrav')) delete properties[key];
    }
  }

  return properties;
}

function installFetchPatch() {
  if (nativeFetch) return;
  nativeFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input instanceof Request ? input.url : '';
    const request = nativeFetch;

    if (url.includes('/cart/add') && isEngravingConfirmed()) {
      const body = init.body;
      if (body instanceof FormData && body.get('id')) {
        const qty = Number(body.get('quantity') || 1);
        const properties = jewelryProperties(body);
        const items = [
          {
            id: Number(body.get('id')),
            quantity: qty,
            properties,
          },
        ];
        const feeId = feeVariantId();
        if (feeId) {
          items.push({
            id: Number(feeId),
            quantity: qty,
            properties: { _for: String(body.get('id')) },
          });
        }
        const payload = { items };
        const sections = body.get('sections');
        if (sections) payload.sections = sections;

        const headers = new Headers(init.headers || {});
        headers.set('Content-Type', 'application/json');

        return request(url, {
          ...init,
          method: init.method || 'POST',
          body: JSON.stringify(payload),
          headers,
          credentials: init.credentials || 'same-origin',
        });
      }
    }

    if (url.includes('/cart/add') && !isEngravingConfirmed()) {
      const body = init.body;
      if (body instanceof FormData) {
        for (const key of [...body.keys()]) {
          if (/^properties\[.*engrav/i.test(key)) body.delete(key);
        }
      }
    }

    const response = await request(input, init);

    if (
      !syncing &&
      !document.querySelector('.amada-cart') &&
      (url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart/add'))
    ) {
      queueMicrotask(() => {
        syncFeeLines();
      });
    }

    return response;
  };
}

function init() {
  installFetchPatch();
  bindField();
  document.addEventListener('shopify:section:load', bindField);
  document.addEventListener('quick-add:ready', bindField);
  document.addEventListener(StandardEvents.productSelect, (event) => {
    if (!hasEngraving() || !isEngravingConfirmed()) return;

    if (event?.promise) {
      event.promise
        .then(({ detail }) => {
          if (detail?.html) {
            const newPp = detail.html.querySelector('product-price[data-amada-base-cents]');
            const newBase = newPp?.getAttribute('data-amada-base-cents');
            if (newBase) {
              document.querySelectorAll('product-price[data-amada-base-cents]').forEach((pp) => {
                pp.setAttribute('data-amada-base-cents', newBase);
              });
            }
          }
          requestAnimationFrame(onEngravingInput);
        })
        .catch(() => {});
    } else {
      requestAnimationFrame(onEngravingInput);
    }
  });
}

init();
